import { z } from 'zod';
import { CursorPaginationSchema } from './shared/pagination.js';
// ============================================================================
// Search Schemas
// ============================================================================

export const SearchRequestSchema = z.object({
  query: z.string().min(1),
  limit: z.number().min(1).max(100).default(20).optional(),
  searchType: z.enum(['tweets', 'users', 'all']).default('all').optional(),
  filters: z
    .object({
      verified: z.boolean().optional(),
      hasMedia: z.boolean().optional(),
      minLikes: z.number().optional(),
      minRetweets: z.number().optional(),
      fromDate: z.string().optional(),
      toDate: z.string().optional(),
    })
    .optional(),
});

export type SearchRequest = z.infer<typeof SearchRequestSchema>;

export const SearchTweetResultSchema = z.object({
  id: z.string(),
  text: z.string(),
  createdAt: z.date(),
  author: z.object({
    id: z.string(),
    name: z.string(),
    username: z.string(),
    profileImageUrl: z.string().url().nullable().optional(),
    verifiedType: z.enum(['none', 'blue', 'business', 'government']).optional(),
  }),
  metrics: z
    .object({
      retweetCount: z.number(),
      replyCount: z.number(),
      likeCount: z.number(),
      quoteCount: z.number(),
    })
    .optional(),
});

export const SearchUserResultSchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  bio: z.string().nullable().optional(),
  profileImageUrl: z.string().url().nullable().optional(),
  verifiedType: z.enum(['none', 'blue', 'business', 'government']).optional(),
  followersCount: z.number().optional(),
  followingCount: z.number().optional(),
});

const SearchPaginationSchema = CursorPaginationSchema.extend({
  totalCount: z.number().int().min(0),
  limit: z.number().int().min(1).max(100).nullable().optional(),
});

export const SearchResponseSchema = z.object({
  tweets: z.array(SearchTweetResultSchema).optional(),
  users: z.array(SearchUserResultSchema).optional(),
  pagination: SearchPaginationSchema,
  searchType: z.enum(['tweets', 'users', 'all']),
});

export type SearchTweetResult = z.infer<typeof SearchTweetResultSchema>;
export type SearchUserResult = z.infer<typeof SearchUserResultSchema>;
export type SearchResponse = z.infer<typeof SearchResponseSchema>;
